// Client-side analytics helper. Every event fans out to three sinks so each tool
// sees the same interactions under the same names:
//   1. our first-party, cookieless /api/analytics collector (always);
//   2. GTM's dataLayer, so the web + server-side (sGTM) containers can fire GA4 /
//      Google Ads / Meta / TikTok tags on landing-page interactions;
//   3. the consent-gated PostHog client (once loaded), for A/B experiment goals.
// Safe to call from anywhere in the browser, before or after GTM/PostHog load.

import posthog from "posthog-js";
import { CLICK_ID_PARAMS, UTM_PARAMS } from "@/lib/attribution";
import type {
  AnalyticsEventMap,
  AnalyticsEventName,
  TrackArgs,
} from "@/lib/events";

type Props = Record<string, unknown>;

function posthogCapture(name: string, props?: Props) {
  if ((posthog as unknown as { __loaded?: boolean }).__loaded === true) {
    posthog.capture(name, props);
  }
}

// Push an event onto GTM's dataLayer. The container processes the queue when it
// loads, so this is safe to call before the GTM script is ready. Tags that touch
// cookies/PII stay gated by Consent Mode inside GTM — pushing here does not bypass
// consent, it only makes the interaction available to the container.
function pushToDataLayer(event: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
}

function collectUtm(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of UTM_PARAMS) {
    const value = params.get(key);
    if (value) utm[key] = value;
  }
  // Presence only, never the identifier. This beacon is not consent-gated, the
  // proxy logs it verbatim, and the platform reads nothing but whether the key
  // is set (LandingPageEventController::clickIdPlatform). The contract's list
  // rather than a copy: a hand-kept one held gclid and fbclid only, so no TikTok
  // click ever reached click_id_platform.
  for (const key of CLICK_ID_PARAMS) {
    if (params.get(key)) utm[key] = "1";
  }
  return utm;
}

// Minted here, in the browser, and NOT on the server.
//
// The whole point of the key is to make a redelivery of one logical event
// harmless. sendBeacon reports no success or failure, so the browser is free to
// retry, and this file already documents two ways one visitor action produces
// two sends (a double-clicked prefetched link, a held-down back button). A key
// generated per server invocation would differ across exactly those retries and
// deduplicate nothing - which is what it did until a reviewer noticed.
function eventKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// The visitor's language and screen, sent with EVERY beacon rather than only
// with pageviews.
//
// They used to ride on trackPageview alone, which left both columns null on 100%
// of stored events - 1,920 of the 3,033 rows on the first full day. Nothing about
// a CTA click, a scroll milestone or an A/B exposure could then be split by
// device or by language, which is most of what anyone asks these events. The
// values are per-page-load constants, so putting them here costs one property
// read per beacon and removes the whole class of question we could not answer.
function pageContext(): Record<string, unknown> {
  return {
    // "nl-NL" on the Dutch site, "en" on the English one; the platform stores
    // the language, so the region suffix is dropped here rather than there.
    locale: document.documentElement.lang.split("-")[0] || undefined,
    screen: `${window.screen.width}x${window.screen.height}`,
  };
}

function send(payload: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  // payload last, so an explicit locale (the route's, which is authoritative)
  // wins over the one read from the document.
  const body = JSON.stringify({ event_key: eventKey(), ...pageContext(), ...payload });
  const url = "/api/analytics";
  // Prefer sendBeacon so the request survives page unloads / navigations.
  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
  } else {
    fetch(url, {
      method: "POST",
      body,
      headers: { "Content-Type": "application/json" },
      keepalive: true,
    }).catch(() => {});
  }
}

// A client-side route change, announced to GTM so the container can fire a GA4
// page_view for it. The site is a Next.js app: after the first load, navigation
// never reloads the document, so the GA4 configuration tag - which fires once, on
// Initialization - never sends another page_view. Every internal page was
// therefore missing from GA4 while Meta, which the container drives off the
// built-in History Change trigger, saw them.
//
// History Change is not enough on its own: it fires the moment the URL changes,
// before Next has swapped the document title, so a page_view built from it
// carries the previous page's name. This runs from the route effect instead,
// after the new route has rendered, and hands the container the title outright.
//
// Deliberately not in the typed event catalog: it is a GTM-only signal. Routing
// it through track() would duplicate the first-party pageview and push a
// synthetic event into PostHog, which captures pageviews itself.
//
// Returns a canceller, which the caller must wire to its effect cleanup. The
// frame's worth of delay is what makes two navigations inside one frame - a
// double-clicked prefetched link, a held-down back button - able to collide:
// both callbacks would read the same final location, dropping the first route
// and reporting the last one twice. Cancelling on re-run leaves exactly one
// push, for the route the visitor actually ended up on.
export function pushVirtualPageView(): (() => void) | undefined {
  if (typeof window === "undefined") return;
  const frame = requestAnimationFrame(() => {
    pushToDataLayer({
      event: "virtual_page_view",
      page_path: `${window.location.pathname}${window.location.search}`,
      page_location: window.location.href,
      page_title: document.title,
    });
  });
  return () => cancelAnimationFrame(frame);
}

export function trackPageview(path: string, locale?: string) {
  send({
    type: "pageview",
    path,
    // Spread conditionally: `locale: undefined` would OVERWRITE the value
    // pageContext() derived from the document, because a spread copies the key
    // whether or not its value is defined. The route's locale is the better
    // answer when there is one; an absent one must fall through, not erase.
    ...(locale ? { locale } : {}),
    referrer: document.referrer || undefined,
    utm: collectUtm(),
  });
}

// Send an analytics event. Generic over the event catalog in events.ts: the name
// must be a declared event and the payload must match its shape (events with an
// empty payload take no second argument). Fans out to all three sinks.
export function track<E extends AnalyticsEventName>(name: E, ...args: TrackArgs<E>) {
  if (typeof window === "undefined") return;
  const props = args[0] as AnalyticsEventMap[E] | undefined;
  send({
    type: "event",
    name,
    path: window.location.pathname,
    utm: collectUtm(),
    props,
  });
  posthogCapture(name, props as Props | undefined);
  // GTM custom-event triggers key off the top-level `event` field; flatten props
  // alongside it so dataLayer variables can read them directly.
  pushToDataLayer({ event: name, ...props });
}

// Build an app URL carrying the `link_id` attribution param the app reads on
// arrival. Use for the anchor `href` so the destination and the reported click
// agree on the link id.
export function ctaHref(href: string, linkId?: string): string {
  if (!linkId) return href;
  return `${href}${href.includes("?") ? "&" : "?"}link_id=${linkId}`;
}

// The anchor id GTM keys CTA reporting off. The container's {{CJS - CTA Click ID}}
// variable resolves `el.closest('a[id^="cta_"]')`, and trigger 8 turns that into
// `clicked_on_sign_up` - a GA4 key event, and the conversion the server-side
// Google Ads tag fires on. An app-bound CTA without a `cta_` id is therefore
// invisible to paid reporting, which is why this is derived rather than opt-in.
export function ctaElementId(linkId?: string): string | undefined {
  return linkId ? `cta_${linkId}` : undefined;
}

// A login link is not a sign-up CTA. Trigger 8 matches the `cta_` prefix blindly,
// so a login anchor wearing one reports every returning sign-in as
// `clicked_on_sign_up` - a GA4 key event and a paid conversion. That is not
// hypothetical: it is why the Jul'26 landing A/B test was unreadable. This site
// tagged all 11 app-bound anchors including both logins, the Framer control tagged
// 2 of 5 and neither login, so the variant arm looked like it turned 22% more
// clicks into sign-up intent when it was counting a different thing. Login anchors
// get their own prefix: the click is still reported as `cta_click` to first-party
// and PostHog, it just stops minting a conversion.
export function loginElementId(linkId: string): string {
  return `login_${linkId}`;
}

// Outbound CTA → the app. Reports one consistent `cta_click` shape to all sinks
// (first-party, GTM dataLayer, PostHog). `cta_href` is the base destination plus
// `link_id` only: AttributionForwarder stamps the click ids and UTMs onto the
// anchor itself after mount, so the URL actually navigated to carries more than
// this. That is deliberate - the reported href stays a stable, low-cardinality
// value to group on, and the ad identifiers reach the platforms that need them
// through the anchor and the first-touch cookie rather than through event props.
export function trackCtaClick(
  linkId: string | undefined,
  href: string,
  extra?: Props,
) {
  track("cta_click", { link_id: linkId ?? null, cta_href: ctaHref(href, linkId), ...extra });
}

// Page-engagement helpers. Thin wrappers over track() so the event names stay
// consistent everywhere and reach all three sinks (first-party, GTM, PostHog).

// A page section scrolled into view. Fired once per section per page load by
// PageEngagement. `section` is a stable, locale-independent id (e.g. "hero").
export function trackSectionView(section: string) {
  track("section_viewed", { section });
}

// A scroll-depth milestone (25 / 50 / 75 / 100) was reached, once each per load.
export function trackScrollDepth(depth: number) {
  track("scroll_depth", { depth });
}

// Begin measuring one page view, and get back the reporter that ends it.
//
// A factory, not a plain track function, because WHERE the visitor was is only
// knowable when they arrive. The dominant way to leave this site is an internal
// link, and by the time the route effect's cleanup runs, the router has already
// advanced window.location to the DESTINATION - so an exit that reads the URL at
// send time reports the page being arrived at, which is precisely backwards for
// the one measurement this event exists to provide. The query string goes the
// same way: collectUtm() at exit time returns the new route's params, not the
// campaign the visitor actually landed on.
//
// Capturing both here makes that mistake unavailable rather than merely
// documented - there is no argument a caller can forget to pass.
//
// First-party leg only, unlike every other helper here. PostHog records its own
// $pageleave natively whenever it captures pageviews, so fanning out would store
// the same departure twice and pay for it twice - and PostHog only exists after
// the visitor answers the consent banner, which is exactly the population this
// event has to cover. GTM has no trigger for any name in events.ts, so the
// dataLayer leg would land on the floor regardless.
export function startPageView(): (dwellMs: number, maxDepth: number) => void {
  if (typeof window === "undefined") return () => {};

  const path = window.location.pathname;
  const utm = collectUtm();
  // Locale too, and for the same reason as the other two. send() derives it
  // from document.documentElement.lang at send time, and a client-side language
  // switch rewrites that attribute while this page view is still open - so an
  // exit would carry the departed page's path with the DESTINATION's language,
  // a row that is internally inconsistent and quietly wrong in every
  // language-segmented figure. Everything this reporter sends describes the
  // moment the page view began; locale is not an exception.
  const { locale } = pageContext();

  return (dwellMs: number, maxDepth: number) => {
    // No zero-dwell guard here, deliberately, and it is not an oversight.
    //
    // One stood here to suppress the phantom exit React StrictMode produces in
    // development, where the discarded first effect cycle sets up and tears down
    // inside one tick. It cost more than it saved: a wall clock stepped backward
    // by NTP or a resume from sleep makes a real visit compute negative, and a
    // browser that coarsens its clock against fingerprinting (Firefox's
    // resistFingerprinting rounds to ~100ms) makes a real fast departure compute
    // exactly zero - and the guard dropped the whole beacon, scroll depth
    // included, in both cases.
    //
    // The phantom it targeted never reaches storage: development has no
    // PLATFORM_API_URL, so the sink discards it as "not-configured" and it costs
    // one console line nobody reads. Losing a real visitor's exit is the larger
    // error, so the guard is gone and the caller measures on a monotonic clock.
    send({
      type: "event",
      name: "page_exited",
      path,
      utm,
      // Conditional, like trackPageview's: a spread copies the key whether or
      // not its value is defined, so an undefined snapshot would ERASE what
      // pageContext() derives inside send() rather than fall through to it.
      ...(locale ? { locale } : {}),
      props: { dwell_ms: dwellMs, max_depth: maxDepth },
    });
  };
}

// A FAQ / accordion item was opened (not fired on close).
export function trackFaqOpen(index: number, question: string) {
  track("faq_opened", { index, question });
}

// Dispatched by PostHogProvider the moment posthog.init() finishes or capturing
// is opted back in. Anything that needs PostHog at page load has to wait for
// this: PostHog does not exist until the visitor answers the consent banner,
// which is always after mount.
export const POSTHOG_READY_EVENT = "tvt:posthog-ready";

// Landing-page A/B split exposure, first-party + GTM legs.
//
// Deliberately does NOT touch PostHog - see registerExperimentArm. Splitting the
// two is what fixes the bug where the arm never reached PostHog at all: this
// runs on mount, PostHog initialises only once OneTrust grants C0002, and that
// is always later, so the PostHog leg of a single combined call silently no-op'd
// every single time. Every event on the arm then went unsegmented, which is the
// whole reason the component exists.
export function trackExperimentExposure(experiment: string, variant: string) {
  if (typeof window === "undefined") return;
  const props: AnalyticsEventMap["experiment_exposure"] = { experiment, variant };
  send({
    type: "event",
    name: "experiment_exposure",
    path: window.location.pathname,
    utm: collectUtm(),
    props,
  });
  pushToDataLayer({ event: "experiment_exposure", ...props });
}

// The PostHog leg of the exposure: register the arm as a super property so every
// subsequent event is segmented by it, then record the exposure itself.
//
// Returns false when PostHog is not loaded, so the caller can wait for
// POSTHOG_READY_EVENT and try again. Consent-safe by construction: PostHog is
// only ever loaded once analytics consent is granted, so a false here is the
// no-consent case and a true is the granted one.
export function registerExperimentArm(experiment: string, variant: string): boolean {
  if ((posthog as unknown as { __loaded?: boolean }).__loaded !== true) return false;
  posthog.register({ ab_variant: variant });
  posthog.capture("experiment_exposure", { experiment, variant });
  return true;
}
